import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  Select,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { FaCalendarAlt, FaColumns, FaPlus, FaSlidersH, FaTable } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import Layout from "../../../components/Layout";
import { CrmTask, TaskStatus } from "../../../models/Task";
import {
  deleteTask,
  getTaskStatuses,
  getTasks,
  moveTask,
} from "../../../services/taskService";
import { getUsers } from "../../../services/userService";
import TaskBoard, { TaskColumn } from "../components/TaskBoard";
import TaskCalendar from "../components/TaskCalendar";
import TaskFormModal from "../components/TaskFormModal";
import TaskStatusManager from "../components/TaskStatusManager";
import TaskTable from "../components/TaskTable";
import { crm } from "../components/crmTheme";

interface UserOption {
  id: string;
  firstname?: string;
  lastname?: string;
  email?: string;
}
const userLabel = (u: UserOption) =>
  `${u.firstname ?? ""} ${u.lastname ?? ""}`.trim() || u.email || "Usuário";

const Tasks = () => {
  const auth = useAuthUser<any>();
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const isAdmin = !!auth?.admin;

  const [statuses, setStatuses] = useState<TaskStatus[]>([]);
  const [tasks, setTasks] = useState<CrmTask[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [userFilter, setUserFilter] = useState<string>("");
  const [loading, setLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<string | undefined>();

  const [taskToDelete, setTaskToDelete] = useState<CrmTask | null>(null);
  const [deleting, setDeleting] = useState(false);
  const cancelRef = useRef(null);
  const toast = useToast();
  const [tabIndex, setTabIndex] = useState(1);

  useEffect(() => {
    if (auth && !auth.admin && auth.position == null) navigate("/");
  }, [auth]);

  useEffect(() => {
    if (isAdmin) getUsers().then((d) => setUsers(d ?? []));
  }, [isAdmin]);

  const load = async () => {
    setLoading(true);
    const [sts, tks] = await Promise.all([
      getTaskStatuses(),
      getTasks({ userId: isAdmin ? userFilter || undefined : undefined }),
    ]);
    setStatuses(sts);
    setTasks(tks);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [userFilter]);

  const columns: TaskColumn[] = useMemo(() => {
    const cols: TaskColumn[] = statuses
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((s) => ({
        status: s,
        tasks: tasks
          .filter((t) => t.taskStatusId === s.id)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
      }));
    const orphans = tasks.filter((t) => !t.taskStatusId);
    if (orphans.length > 0) {
      cols.unshift({
        status: { id: undefined, name: "Sem status", order: -1 },
        tasks: orphans,
      });
    }
    return cols;
  }, [statuses, tasks]);

  const handleMove = async (task: CrmTask, target: TaskColumn) => {
    const order = target.tasks.length;
    setTasks((prev) =>
      prev.map((t) =>
        t.id === task.id ? { ...t, taskStatusId: target.status.id, order } : t
      )
    );
    await moveTask(task.id!, { taskStatusId: target.status.id ?? null, order });
    load();
  };

  const confirmDelete = async () => {
    if (!taskToDelete?.id) return;
    setDeleting(true);
    try {
      await deleteTask(taskToDelete.id);
      toast({ title: "Task excluída.", status: "success", duration: 2500 });
      setTaskToDelete(null);
      await load();
    } catch {
      toast({ title: "Erro ao excluir a task.", status: "error" });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box height={isMobile ? undefined : "100vh"} overflow="hidden">
      <Layout>
        <Box p="24px" bg={crm.pageBg} h="100%">
          {/* Page header */}
          <Flex justify="space-between" align="flex-start" mb="20px" flexWrap="wrap" gap="12px">
            <Flex align="center" gap="12px">
              <Box w="4px" h="32px" bg={crm.brand} borderRadius="full" />
              <Box>
                <Text fontSize="28px" fontWeight="700" color={crm.text} lineHeight="1.1">
                  Minhas Tasks
                </Text>
                <Text color={crm.textMuted} fontSize="sm" mt="2px">
                  {isAdmin
                    ? "Visão de todas as tasks. Filtre por usuário se quiser."
                    : "Suas tasks atribuídas."}
                </Text>
              </Box>
            </Flex>
            <Flex gap="10px" flexWrap="wrap">
              {isAdmin && (
                <Select
                  maxW="220px"
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  bg={crm.surface}
                  borderColor={crm.border}
                  borderRadius="10px"
                  fontSize="sm"
                >
                  <option value="">Todos os usuários</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {userLabel(u)}
                    </option>
                  ))}
                </Select>
              )}
              <Button
                leftIcon={<FaSlidersH />}
                variant="outline"
                borderColor={crm.border}
                color={crm.text}
                _hover={{ bg: crm.brandSoft, borderColor: crm.brand }}
                borderRadius="10px"
                fontWeight="500"
                onClick={() => setStatusOpen(true)}
              >
                Gerenciar status
              </Button>
              <Button
                leftIcon={<FaPlus />}
                bg={crm.brand}
                color="white"
                _hover={{ bg: crm.brandDark }}
                borderRadius="10px"
                fontWeight="600"
                onClick={() => {
                  setEditingTaskId(undefined);
                  setFormOpen(true);
                }}
              >
                Nova task
              </Button>
            </Flex>
          </Flex>

          {loading ? (
            <Flex justify="center" align="center" minH="300px">
              <Spinner color={crm.brand} />
            </Flex>
          ) : (
            <Tabs
              index={tabIndex}
              onChange={setTabIndex}
              variant="unstyled"
            >
              {/* Segmented pill tab control */}
              <TabList
                mb="20px"
                bg={crm.subtle}
                p="4px"
                borderRadius="10px"
                display="inline-flex"
                border={`1px solid ${crm.border}`}
              >
                {[
                  { icon: <FaTable />, label: "Tabela" },
                  { icon: <FaColumns />, label: "Colunas" },
                  { icon: <FaCalendarAlt />, label: "Calendário" },
                ].map(({ icon, label }, i) => (
                  <Tab
                    key={label}
                    borderRadius="8px"
                    px="16px"
                    py="6px"
                    fontSize="sm"
                    fontWeight={tabIndex === i ? "600" : "400"}
                    color={tabIndex === i ? crm.brand : crm.textMuted}
                    bg={tabIndex === i ? crm.surface : "transparent"}
                    boxShadow={tabIndex === i ? crm.shadowSm : "none"}
                    _hover={{ color: tabIndex === i ? crm.brand : crm.text }}
                    transition="all .15s"
                  >
                    <Flex align="center" gap="6px">
                      {icon}
                      {label}
                    </Flex>
                  </Tab>
                ))}
              </TabList>
              <TabPanels>
                <TabPanel p={0}>
                  <TaskTable
                    tasks={tasks}
                    onEdit={(t) => {
                      setEditingTaskId(t.id);
                      setFormOpen(true);
                    }}
                    onDelete={(t) => setTaskToDelete(t)}
                  />
                </TabPanel>
                <TabPanel p={0}>
                  <TaskBoard
                    columns={columns}
                    onMove={handleMove}
                    onTaskClick={(t) => {
                      setEditingTaskId(t.id);
                      setFormOpen(true);
                    }}
                    onTaskDelete={(t) => setTaskToDelete(t)}
                  />
                </TabPanel>
                <TabPanel p={0}>
                  <TaskCalendar
                    tasks={tasks}
                    onTaskClick={(t) => {
                      setEditingTaskId(t.id);
                      setFormOpen(true);
                    }}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}
        </Box>

        <TaskFormModal
          isOpen={formOpen}
          onClose={() => setFormOpen(false)}
          onSaved={load}
          statuses={statuses}
          isAdmin={isAdmin}
          taskId={editingTaskId}
          onManageStatus={() => setStatusOpen(true)}
        />
        <TaskStatusManager
          isOpen={statusOpen}
          onClose={() => setStatusOpen(false)}
          onChanged={load}
        />

        <AlertDialog
          isOpen={!!taskToDelete}
          leastDestructiveRef={cancelRef}
          onClose={() => setTaskToDelete(null)}
          isCentered
        >
          <AlertDialogOverlay>
            <AlertDialogContent borderRadius="16px" boxShadow={crm.shadowLg}>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Excluir task
              </AlertDialogHeader>
              <AlertDialogBody>
                Tem certeza que deseja excluir a task{" "}
                <b>{taskToDelete?.title}</b>?
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} variant="ghost" onClick={() => setTaskToDelete(null)}>
                  Cancelar
                </Button>
                <Button
                  colorScheme="red"
                  onClick={confirmDelete}
                  isLoading={deleting}
                  ml={3}
                  borderRadius="10px"
                >
                  Excluir
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Layout>
    </Box>
  );
};

export default Tasks;
